import React, { createContext, JSX, useCallback, useContext, useRef } from 'react';
import { SetSnackType, SeverityType, Snack } from 'components/Snack';
import { ApiErrorDetails } from 'services/api-client';
import { useAuth } from 'hooks/AuthContext';
import { AppStateContextType } from './appStateTypes';
import { useAppStateLogic } from './useAppStateLogic';

const AppStateContext = createContext<AppStateContextType | null>(null);

export function AppStateProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const appState = useAppStateLogic();
  const { apiClient } = useAuth();
  const snackRef = useRef<{ setSnack: SetSnackType }>(null);

  appState.setSnack = useCallback(
    (message: string, severity?: SeverityType) => {
      snackRef.current?.setSnack(message, severity);
    },
    [snackRef]
  );

  apiClient.setErrorCallback((error: ApiErrorDetails) => {
    if (error.status === 401) {
      appState.setSnack('Authentication token expired or invalid. Please login again.', 'error');
    } else if (error.status === 403) {
      appState.setSnack('Access denied', 'error');
    } else if (error.status >= 500) {
      appState.setSnack('Server error. Please try again later.', 'error');
    } else {
      appState.setSnack(
        `Error: ${('message' in error && error.message) || 'An unexpected error occurred.'}`,
        'error'
      );
    }
  });
  apiClient.setStreamingFeedbackCallbacks({
    onSlowStart: (message: string) => appState.setSnack(message, 'info'),
    onUnavailable: (message: string) => appState.setSnack(message, 'error'),
  });

  return (
    <AppStateContext.Provider value={appState}>
      {children}
      <Snack ref={snackRef} />
    </AppStateContext.Provider>
  );
}

export function useAppState(): AppStateContextType {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
}

export type { AppState, AppStateActions, AppStateContextType, RouteState } from './appStateTypes';
export {
  useAppInitializing,
  useRouteState,
  useSelectedCandidate,
  useSelectedEmployer,
  useSelectedJob,
  useSelectedResume,
} from './appStateSelectors';
